import { UsageEventsApi } from './ApiService';
import { v4 as uuidv4 } from 'uuid';

/**
 * UsageEventsTracker
 * Lightweight front-end tracker for generic usage events.
 * Responsibilities:
 *  - Generate / persist a sessionId (per tab or across tabs via localStorage)
 *  - Provide correlation grouping helper (startCorrelation -> returns correlationId reused across related events)
 *  - Queue + optional batch flush (debounced) for high-volume events
 *  - Enforce & document payloadJson schema.
 *
 * payloadJson schema (version 1):
 *  {
 *    "version": 1,
 *    "action": string,                // REQUIRED concise action name (e.g. "open", "click", "submit", "view")
 *    "category": string,              // High-level feature area (e.g. "chat", "courses", "navigation", "theme")
 *    "entityType": string | null,     // Domain entity type involved (e.g. "course", "message", "assignment")
 *    "entityId": string | number | null, // ID of entity if relevant
 *    "label": string | null,          // Human-readable label or UI element id
 *    "value": number | null,          // Numeric value for metrics (e.g. token count, duration)
 *    "metadata": {                    // Arbitrary additional fields (flat or nested) - MUST be JSON serializable
 *        ...
 *    }
 *  }
 *  Additional top-level event fields: userId (server side from token or explicit), eventType, sessionId, correlationId, occurredAt.
 *  eventType naming convention: UPPER_SNAKE summarizing feature + action (e.g. PAGE_VIEW, NAV_CLICK, THEME_CHANGE, CHAT_PROMPT_SENT)
 *
 * Additional schema details & examples:
 * Example: THEME_CHANGE
 *  eventType: 'THEME_CHANGE'
 *  payload: {
 *    action: 'change', category: 'theme', entityType: 'theme', entityId: 'dark',
 *    label: 'Theme Switcher', value: null, metadata: { previous: 'light', next: 'dark' }
 *  }
 * Example: PAGE_VIEW
 *  eventType: 'PAGE_VIEW'
 *  payload: { action:'view', category:'navigation', entityType:'page', entityId:'/dashboard', label:'Dashboard' }
 * Example: NAV_CLICK
 *  eventType: 'NAV_CLICK'
 *  payload: { action:'click', category:'navigation', entityType:'link', entityId:'/courses', label:'Courses Link' }
 * Example: CHAT_PROMPT_SENT
 *  payload: { action:'submit', category:'chat', entityType:'message', value: tokens, metadata:{ model, promptTokens, completionTokens } }
 */

/** Reliability strategies implemented (see flush logic):
 * 1. Durable queue persisted in localStorage; events only removed on confirmed 2xx (resp.ok).
 * 2. Non-ok or network errors throw -> queue retained.
 * 3. Attempt counting per event (__attempts). After MAX_ATTEMPTS moves to dead-letter store (also persisted) for forensic inspection.
 * 4. Exponential backoff with jitter after each failed flush (capped) to avoid tight retry storms.
 * 5. Page lifecycle delivery: visibility-hidden & pagehide trigger a flush (keepalive best-effort) without clearing on failure.
 * 6. Concurrency guard (isFlushing) prevents overlapping flushes marking each other canceled.
 * 7. Structured diagnostic logging: start, success (attempt range), failure (failureCount, aborted flag), backoff scheduling.
 * 8. Force flush option for critical paths (recordImmediate / recordQueued second param { forceFlush:true }).
 * Future optional enhancements (not yet implemented):
 *  - Split very large batches when near keepalive payload limits.
 *  - Route-change pre-navigation flush.
 *  - Periodic dead-letter reporting endpoint or UI panel.
 */

// Persist sessionId across tab reloads; new per tab if desired => choose key
const SESSION_KEY = 'usage_session_id_v1';
function getOrCreateSessionId() {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY) || localStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const id = uuidv4();
    sessionStorage.setItem(SESSION_KEY, id);
    // cross-tab resilience
    localStorage.setItem(SESSION_KEY, id);
    return id;
  } catch {
    return uuidv4();
  }
}

const sessionId = getOrCreateSessionId();

class CorrelationManager {
  constructor() { this.active = new Map(); }
  start(label) {
    const id = uuidv4();
    this.active.set(label, id);
    return id;
  }
  get(label) { return this.active.get(label); }
  clear(label) { this.active.delete(label); }
  getAny() {
    // Get any active correlation ID (prioritize most recently used)
    const entries = Array.from(this.active.entries());
    return entries.length > 0 ? entries[entries.length - 1][1] : null;
  }
}

const correlationMgr = new CorrelationManager();

// Persistent queue keys
const QUEUE_STORAGE_KEY = 'usage_events_queue_v1';
const LAST_FLUSH_KEY = 'usage_events_last_flush_ts';
const DEAD_LETTER_KEY = 'usage_events_dead_letter_v1';
const MAX_ATTEMPTS = 5;

class PersistentEventQueue {
  constructor() {
    this.queue = this._load() || [];
    this.flushTimer = null;
    // REMOVED: this.maxBatchSize = 20; - No longer limiting batch size
    this.maxIntervalMs = 10 * 60 * 1000; // 10 minutes per spec
    this.visibilityFlushDebounceMs = 1500;
    this.visibilityTimer = null;
    this.isFlushing = false;
    this.deadLetter = this._loadDeadLetter();
    this.failureCount = 0; // consecutive flush failures
    this.idleShortTimer = null;
    this._scheduleInterval();
    // Startup quick flush attempt if residual queue
    if (this.queue.length) {
      setTimeout(() => {
        if (this.queue.length) {
          this.flush({ reason: 'startup' });
        }
      }, 1000);
    }
  }

  _load() {
    try {
      const raw = localStorage.getItem(QUEUE_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
      return [];
    } catch {
      return [];
    }
  }

  _persist() {
    try {
      localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(this.queue));
      localStorage.setItem(DEAD_LETTER_KEY, JSON.stringify(this.deadLetter));
    } catch (e) {
      console.warn('UsageEvents: Failed to persist queue', e);
    }
  }

  _loadDeadLetter() {
    try {
      const raw = localStorage.getItem(DEAD_LETTER_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  _scheduleInterval() {
    if (this.flushTimer) clearTimeout(this.flushTimer);
    const lastFlush = parseInt(localStorage.getItem(LAST_FLUSH_KEY) || '0', 10) || 0;
    const now = Date.now();
    const elapsed = now - lastFlush;
    const remaining = Math.max(0, this.maxIntervalMs - elapsed);
    this.flushTimer = setTimeout(() => {
      this.flush({ reason: 'interval' });
    }, remaining || this.maxIntervalMs);
  }

  enqueue(ev) {
    if (!ev.__attempts) ev.__attempts = 0;
    this.queue.push(ev);
    this._persist();
    // REMOVED: Batch size check - events only flush on timer or explicit triggers
    // Debounced short idle flush (5s) so small trickle events still go out
    if (this.idleShortTimer) clearTimeout(this.idleShortTimer);
    this.idleShortTimer = setTimeout(() => {
      if (!this.isFlushing && this.queue.length) {
        this.flush({ reason: 'idle-short' });
      }
    }, 5000);
  }

  async flush({ reason = 'manual', keepalive = false } = {}) {
    if (this.isFlushing) {
      return;
    }
    if (!this.queue.length) {
      this._scheduleInterval();
      return;
    }
    const batch = this.queue.map(e => ({ ...e })); // shallow copy
    const flushId = `${Date.now()}-${Math.random().toString(36).slice(2,8)}`;
    this.isFlushing = true;
    // Check if there's an active correlation ID to pass along
    const correlationId = correlationMgr.getAny();
    // Attempt send
    try {
      await UsageEventsApi.recordEventsBatch(batch, { keepalive, correlationId });
      this.queue = [];
      this._persist();
      localStorage.setItem(LAST_FLUSH_KEY, String(Date.now()));
      this.failureCount = 0; // reset on success
    } catch (e) {
      const aborted = e && (e.name === 'AbortError' || /abort/i.test(e.message || ''));
      const isAuthError = e && (e.status === 401 || e.status === 403);
      console.warn('UsageEvents: flush failed', { flushId, aborted, name: e?.name, msg: e?.message, status: e?.status, reason, size: batch.length, failureCount: this.failureCount + 1, isAuthError });
      
      // If authentication/authorization error (401/403), clear the queue and stop retrying
      // These errors won't be fixed by retrying - user needs to log in again
      if (isAuthError) {
        // eslint-disable-next-line no-console
        console.warn('[UsageEvents] Authentication error - clearing queue and stopping retries', { status: e.status, queueSize: this.queue.length });
        this.queue = [];
        this._persist();
        this.failureCount = 0; // Reset failure count
        this.isFlushing = false;
        // Don't schedule any retries
        return;
      }
      
      // Increment attempts on queued events (still in this.queue, not cleared yet)
      this.queue.forEach(ev => { ev.__attempts = (ev.__attempts || 0) + 1; });
      // If repeated failures, attempt per-event fallback to salvage good events (e.g., one bad causing 400)
      if ((this.failureCount + 1) >= 2 && this.queue.length > 1) {
        const salvageable = [];
        const remaining = [];
        for (const ev of this.queue) {
          try {
            // best-effort single send (no keepalive; rely on auth wrapper)
            // we intentionally do not await all in parallel to reduce burst
            // eslint-disable-next-line no-await-in-loop
            const r = await UsageEventsApi.recordEvent({ ...ev });
            if (r && r.ok) {
              salvageable.push(ev);
            } else {
              remaining.push(ev);
            }
          } catch (singleErr) {
            remaining.push(ev);
          }
        }
        this.queue = remaining;
      }
      // Partition: move over-attempted to dead letter
      const stillLive = []; const promoted = [];
      for (const ev of this.queue) {
        if ((ev.__attempts || 0) >= MAX_ATTEMPTS) {
          promoted.push(ev);
        } else {
          stillLive.push(ev);
        }
      }
      if (promoted.length) {
        this.deadLetter.push(...promoted.map(p => ({ ...p, __droppedAt: new Date().toISOString() })));
        console.warn('[UsageEvents] Moved to dead-letter', { count: promoted.length, deadLetterSize: this.deadLetter.length });
      }
      this.queue = stillLive;
      this._persist();
      this.failureCount += 1;
    } finally {
      this.isFlushing = false;
      this._scheduleInterval();
      // Apply backoff if failures >0
      if (this.failureCount > 0 && this.queue.length) {
        const base = Math.min(30000, 1000 * Math.pow(2, this.failureCount)); // cap 30s
        const jitter = Math.random() * 0.3 * base; // 0-30% jitter
        const delay = base + jitter;
        // eslint-disable-next-line no-console
        console.log('[UsageEvents] Scheduling retry with backoff', { delayMs: Math.round(delay), failureCount: this.failureCount });
        if (this.flushTimer) clearTimeout(this.flushTimer);
        this.flushTimer = setTimeout(() => this.flush({ reason: 'backoff-retry' }), delay);
      }
    }
  }

  scheduleVisibilityFlush() {
    if (this.visibilityTimer) clearTimeout(this.visibilityTimer);
    this.visibilityTimer = setTimeout(() => {
      this.flush({ reason: 'visibility-debounce' });
    }, this.visibilityFlushDebounceMs);
  }
}

const eventQueue = new PersistentEventQueue();

// Visibility / unload handlers to improve delivery reliability
if (typeof window !== 'undefined') {
  const handleVisibility = () => {
    if (document.visibilityState === 'hidden') {
      eventQueue.flush({ reason: 'visibility-hidden', keepalive: true });
    } else if (document.visibilityState === 'visible') {
      eventQueue.scheduleVisibilityFlush();
    }
  };
  document.addEventListener('visibilitychange', handleVisibility);
  window.addEventListener('pagehide', () => {
    eventQueue.flush({ reason: 'pagehide', keepalive: true });
  });
}

function buildEvent({ eventType, payload, userId, correlationId, occurredAt }) {
  return {
    userId, // optional; backend may infer
    eventType,
    sessionId,
    correlationId: correlationId || uuidv4(),
    payloadJson: JSON.stringify({ version:1, ...payload }),
    occurredAt: occurredAt || new Date().toISOString(),
  };
}

export const UsageEventsTracker = {
  sessionId,
  startCorrelation: (label) => correlationMgr.start(label),
  getCorrelation: (label) => correlationMgr.get(label),
  clearCorrelation: (label) => correlationMgr.clear(label),

  /** Unified enqueue (recordImmediate now just enqueues) */
  recordImmediate({ eventType, payload, userId, correlationId, occurredAt }, { forceFlush = false } = {}) {
    eventQueue.enqueue(buildEvent({ eventType, payload, userId, correlationId, occurredAt }));
    if (forceFlush) {
      eventQueue.flush({ reason: 'forceFlush-immediate' });
    }
  },

  recordQueued({ eventType, payload, userId, correlationId, occurredAt }, { forceFlush = false } = {}) {
    eventQueue.enqueue(buildEvent({ eventType, payload, userId, correlationId, occurredAt }));
    if (forceFlush) {
      eventQueue.flush({ reason: 'forceFlush-queued' });
    }
  },

  flush: (opts) => eventQueue.flush(opts),

  // ========== STANDARDIZED HELPER METHODS ==========

  /**
   * Record a page view event (route change or initial load)
   * @param {string} path - Current route path (e.g., '/dashboard', '/courses/123')
   * @param {string} [referrer] - Previous route path or external referrer
   * @param {object} [options] - Additional options (userId, forceFlush)
   */
  recordPageView(path, referrer = null, options = {}) {
    const payload = {
      action: 'view',
      category: 'navigation',
      entityType: 'page',
      entityId: path,
      label: path,
      value: null,
      metadata: referrer ? { referrer } : {}
    };
    this.recordQueued({
      eventType: 'PAGE_VIEW',
      payload,
      userId: options.userId,
      correlationId: options.correlationId,
      occurredAt: options.occurredAt
    }, { forceFlush: options.forceFlush || false });
  },

  /**
   * Record a user interaction event (click, select, complete, etc.)
   * @param {string} eventType - Event type in UPPER_SNAKE_CASE (e.g., 'COURSE_SELECT', 'ASSIGNMENT_COMPLETE')
   * @param {object} payload - Event payload following schema
   * @param {string} payload.action - Action name (click, select, complete, etc.)
   * @param {string} payload.category - Feature category (courses, assignments, documents, etc.)
   * @param {string} [payload.entityType] - Entity type (course, assignment, document, etc.)
   * @param {string|number} [payload.entityId] - Entity ID
   * @param {string} [payload.label] - Human-readable label
   * @param {number} [payload.value] - Numeric value
   * @param {object} [payload.metadata] - Additional metadata
   * @param {object} [options] - Additional options (userId, correlationId, forceFlush)
   */
  recordUserAction(eventType, payload, options = {}) {
    // Ensure payload has required fields
    const normalizedPayload = {
      action: payload.action,
      category: payload.category,
      entityType: payload.entityType || null,
      entityId: payload.entityId || null,
      label: payload.label || null,
      value: payload.value || null,
      metadata: payload.metadata || {}
    };

    this.recordQueued({
      eventType,
      payload: normalizedPayload,
      userId: options.userId,
      correlationId: options.correlationId,
      occurredAt: options.occurredAt
    }, { forceFlush: options.forceFlush || false });

    // Feed breadcrumb trail for error context
    try {
      const { getErrorReportingService } = require('./errorReportingBridge');
      const ers = getErrorReportingService();
      if (ers) {
        ers.addBreadcrumb(
          normalizedPayload.category || 'action',
          `${eventType}: ${normalizedPayload.action || ''} ${normalizedPayload.label || ''}`.trim(),
          { eventType, entityType: normalizedPayload.entityType, entityId: normalizedPayload.entityId }
        );
      }
    } catch (_) { /* silent */ }
  },

  /**
   * Record a performance metric event
   * @param {string} metricType - Type of performance metric (page_load, api_response, etc.)
   * @param {object} data - Metric data
   * @param {number} data.duration - Duration in milliseconds
   * @param {string} [data.url] - URL or route associated with metric
   * @param {object} [data.metadata] - Additional metric metadata
   * @param {object} [options] - Additional options (userId, forceFlush)
   */
  recordPerformance(metricType, data, options = {}) {
    const payload = {
      action: 'measure',
      category: 'performance',
      entityType: metricType,
      entityId: data.url || null,
      label: data.url || metricType,
      value: Math.round(data.duration),
      metadata: {
        duration: Math.round(data.duration),
        ...data.metadata
      }
    };

    this.recordQueued({
      eventType: `PERFORMANCE_${metricType.toUpperCase()}`,
      payload,
      userId: options.userId,
      correlationId: options.correlationId,
      occurredAt: options.occurredAt
    }, { forceFlush: options.forceFlush || false });
  }
};

export default UsageEventsTracker;
